import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import {
  FiSettings,
  FiCalendar,
  FiUser,
  FiPhone,
  FiMail,
  FiCopy,
  FiCheck,
  FiRefreshCw,
  FiAlertCircle,
  FiSend,
  FiFileText,
  FiCheckCircle,
  FiClock,
  FiTrendingUp,
} from 'react-icons/fi';
import { formatCurrency } from '../utils/currency';
import TableScrollContainer from '../components/TableScrollContainer';
import './Configuracoes.css';

function Configuracoes() {
  const [activeTab, setActiveTab] = useState('fechamento'); // 'fechamento' | 'regras'

  const currentYearMonth = new Date().toISOString().slice(0, 7);
  const [selectedMonth, setSelectedMonth] = useState(currentYearMonth);

  const [config, setConfig] = useState({
    dia_fechamento_mes: 25,
    dia_pagamento_comissao: 10,
    percentual_comissao: 20,
    tipo_periodo_fechamento: 'ciclo_fechamento',
    responsavel_nome: 'Franci Kelli Müller',
    responsavel_telefone: '',
    responsavel_email: 'fran@franbeauty.com.br',
    responsavel_cargo: 'Proprietária / Esteticista',
    mensagem_fechamento_modelo: '',
  });

  const [fechamento, setFechamento] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [syncing, setSyncing] = useState(false);
  const [feedback, setFeedback] = useState(null);
  const [copied, setCopied] = useState(false);

  // Carregar dados de configurações
  const fetchConfiguracoes = useCallback(async () => {
    try {
      const res = await axios.get('/api/configuracoes');
      setConfig(res.data);
    } catch (err) {
      console.error('Erro ao carregar configurações:', err);
    }
  }, []);

  // Carregar dados de fechamento para o mês selecionado
  const fetchFechamento = useCallback(async (mes) => {
    setLoading(true);
    try {
      const res = await axios.get(`/api/configuracoes/fechamento?mes=${mes}`);
      setFechamento(res.data);
    } catch (err) {
      console.error('Erro ao calcular fechamento:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchConfiguracoes();
  }, [fetchConfiguracoes]);

  useEffect(() => {
    fetchFechamento(selectedMonth);
  }, [fetchFechamento, selectedMonth]);

  const handleSaveConfig = async (e) => {
    e.preventDefault();
    setSaving(true);
    setFeedback(null);
    try {
      const res = await axios.put('/api/configuracoes', config);
      setConfig(res.data);
      setFeedback({
        type: 'success',
        message: 'Configurações e regras de fechamento salvas com sucesso!',
      });
      // Recarregar fechamento com as novas regras
      fetchFechamento(selectedMonth);
    } catch (err) {
      setFeedback({
        type: 'error',
        message: err.response?.data?.error || 'Erro ao salvar configurações.',
      });
    } finally {
      setSaving(false);
    }
  };

  const handleSyncFinancial = async () => {
    setSyncing(true);
    setFeedback(null);
    try {
      const res = await axios.post('/api/configuracoes/fechamento/sincronizar', {
        mes: selectedMonth,
      });
      setFeedback({
        type: 'success',
        message: res.data.message || 'Comissão sincronizada no financeiro!',
      });
      fetchFechamento(selectedMonth);
    } catch (err) {
      setFeedback({
        type: 'error',
        message: err.response?.data?.error || 'Erro ao sincronizar comissão.',
      });
    } finally {
      setSyncing(false);
    }
  };

  const handleCopyMessage = () => {
    if (!fechamento?.mensagem_formatada) return;
    navigator.clipboard.writeText(fechamento.mensagem_formatada).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 3000);
    });
  };

  // Gerar opções de meses (últimos 12 meses + próximos 2)
  const monthOptions = [];
  const curr = new Date();
  for (let i = -10; i <= 2; i++) {
    const d = new Date(curr.getFullYear(), curr.getMonth() + i, 1);
    const val = d.toISOString().slice(0, 7);
    const label = d.toLocaleDateString('pt-BR', { month: 'long', year: 'numeric' });
    monthOptions.push({
      value: val,
      label: label.charAt(0).toUpperCase() + label.slice(1),
    });
  }

  return (
    <div className="configuracoes-page">
      <div className="config-header">
        <div className="config-title-box">
          <h1>
            <FiSettings className="title-icon" /> Configurações & Fechamento
          </h1>
          <p className="config-subtitle">
            Configure a regra de fechamento do mês, data limite de corte, comissões e envie o
            relatório consolidado para o responsável via WhatsApp ou E-mail.
          </p>
        </div>

        {/* Abas de Navegação */}
        <div className="config-tabs">
          <button
            type="button"
            className={`tab-btn ${activeTab === 'fechamento' ? 'active' : ''}`}
            onClick={() => setActiveTab('fechamento')}
          >
            <FiTrendingUp size={16} />
            <span>Fechamento do Mês</span>
          </button>
          <button
            type="button"
            className={`tab-btn ${activeTab === 'regras' ? 'active' : ''}`}
            onClick={() => setActiveTab('regras')}
          >
            <FiSettings size={16} />
            <span>Regras & Responsável</span>
          </button>
        </div>
      </div>

      {feedback && (
        <div className={`feedback-banner ${feedback.type}`}>
          {feedback.type === 'success' ? <FiCheckCircle size={20} /> : <FiAlertCircle size={20} />}
          <span>{feedback.message}</span>
        </div>
      )}

      {/* ==================== ABA 1: FECHAMENTO DO MÊS ==================== */}
      {activeTab === 'fechamento' && (
        <div className="tab-content fechamento-tab">
          {/* Barra de Controle de Mês & Regra Ativa */}
          <div className="fechamento-control-bar">
            <div className="month-selector-group">
              <label htmlFor="mes-select">
                <FiCalendar /> Mês de Referência:
              </label>
              <select
                id="mes-select"
                value={selectedMonth}
                onChange={(e) => setSelectedMonth(e.target.value)}
                className="month-select"
              >
                {monthOptions.map((opt) => (
                  <option key={opt.value} value={opt.value}>
                    {opt.label}
                  </option>
                ))}
              </select>
            </div>

            {fechamento && (
              <div className="fechamento-rule-badge">
                <span className="badge-title">Regra Ativa:</span>
                <span>
                  Corte todo dia <strong>{config.dia_fechamento_mes}</strong> • Comissão de{' '}
                  <strong>{config.percentual_comissao}%</strong> • Pagamento previsto dia{' '}
                  <strong>{config.dia_pagamento_comissao}</strong>
                </span>
              </div>
            )}
          </div>

          {loading ? (
            <div className="loading-card">
              <FiRefreshCw className="spinning" size={24} />
              <span>Calculando apuração do fechamento de comissão...</span>
            </div>
          ) : fechamento ? (
            <>
              {/* Status Banner do Fechamento */}
              <div className={`status-banner ${fechamento.status.fechado ? 'closed' : 'open'}`}>
                <div className="status-info">
                  <div className="status-badge-container">
                    <span className={`status-pill ${fechamento.status.fechado ? 'closed' : 'open'}`}>
                      {fechamento.status.fechado ? <FiCheck size={14} /> : <FiClock size={14} />}
                      {fechamento.status.label}
                    </span>
                  </div>
                  <div className="period-details">
                    <span>
                      Período apurado: <strong>{fechamento.periodo.formatado}</strong>
                    </span>
                    <span className="bullet-sep">•</span>
                    <span>
                      Data de Fechamento: <strong>{fechamento.data_fechamento_formatada}</strong>
                    </span>
                    <span className="bullet-sep">•</span>
                    <span>
                      Pagamento Previsto: <strong>{fechamento.data_pagamento_formatada}</strong>
                    </span>
                  </div>
                </div>

                <div className="responsible-summary">
                  <span className="resp-label">Responsável pelo Recebimento:</span>
                  <strong className="resp-name">{fechamento.responsavel.nome || 'Não definido'}</strong>
                  {fechamento.responsavel.telefone && (
                    <span className="resp-phone">
                      <FiPhone size={12} /> {fechamento.responsavel.telefone}
                    </span>
                  )}
                </div>
              </div>

              {/* Grid de Indicadores e Totais da Regra de Fechamento */}
              <div className="fechamento-kpi-grid">
                <div className="kpi-card receita">
                  <div className="kpi-header">
                    <span>Faturamento Bruto</span>
                    <span className="kpi-icon">💰</span>
                  </div>
                  <h3 className="kpi-value">{formatCurrency(fechamento.totais.faturamento_bruto)}</h3>
                  <p className="kpi-detail">{fechamento.totais.qtd_atendimentos} atendimentos realizados</p>
                </div>

                <div className="kpi-card comissao-destaque">
                  <div className="kpi-header">
                    <span>Total de Comissão ({fechamento.totais.percentual_comissao}%)</span>
                    <span className="kpi-icon">⭐</span>
                  </div>
                  <h3 className="kpi-value highlight">{formatCurrency(fechamento.totais.total_comissao)}</h3>
                  <p className="kpi-detail">Regra somada para o responsável</p>
                </div>

                <div className="kpi-card despesas">
                  <div className="kpi-header">
                    <span>Despesas Operacionais</span>
                    <span className="kpi-icon">📉</span>
                  </div>
                  <h3 className="kpi-value">{formatCurrency(fechamento.totais.despesas_operacionais)}</h3>
                  <p className="kpi-detail">{fechamento.itens_despesa?.length || 0} lançamentos de custo</p>
                </div>

                <div className="kpi-card lucro">
                  <div className="kpi-header">
                    <span>Lucro Líquido</span>
                    <span className="kpi-icon">💵</span>
                  </div>
                  <h3 className="kpi-value">{formatCurrency(fechamento.totais.lucro_liquido)}</h3>
                  <p className="kpi-detail">Receita - Despesas - Comissões</p>
                </div>
              </div>

              {/* Seção de Ações Rápidas de Envio */}
              <div className="dispatch-actions-card">
                <div className="dispatch-header">
                  <div>
                    <h3>🚀 Enviar Fechamento para o Responsável</h3>
                    <p>
                      Envie o resumo detalhado do fechamento com 1 clique para{' '}
                      <strong>{fechamento.responsavel.nome}</strong>.
                    </p>
                  </div>
                </div>

                <div className="action-buttons-row">
                  {fechamento.whatsapp_link ? (
                    <a
                      href={fechamento.whatsapp_link}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="action-btn btn-whatsapp"
                    >
                      <FiSend size={18} />
                      <span>Enviar no WhatsApp</span>
                    </a>
                  ) : (
                    <button
                      type="button"
                      className="action-btn btn-whatsapp disabled"
                      onClick={() => setActiveTab('regras')}
                      title="Cadastre o WhatsApp do responsável na aba de regras"
                    >
                      <FiSend size={18} />
                      <span>Cadastrar WhatsApp do Responsável</span>
                    </button>
                  )}

                  {fechamento.mailto_link && (
                    <a href={fechamento.mailto_link} className="action-btn btn-email">
                      <FiMail size={18} />
                      <span>Enviar por E-mail</span>
                    </a>
                  )}

                  <button type="button" className="action-btn btn-copy" onClick={handleCopyMessage}>
                    {copied ? <FiCheck size={18} /> : <FiCopy size={18} />}
                    <span>{copied ? 'Copiado!' : 'Copiar Resumo'}</span>
                  </button>

                  <button
                    type="button"
                    className="action-btn btn-sync"
                    onClick={handleSyncFinancial}
                    disabled={syncing}
                  >
                    <FiRefreshCw className={syncing ? 'spinning' : ''} size={18} />
                    <span>{syncing ? 'Sincronizando...' : 'Sincronizar no Financeiro'}</span>
                  </button>
                </div>

                {/* Prévia da Mensagem Formatada */}
                <div className="message-preview-container">
                  <div className="preview-label">
                    <FiFileText size={14} /> Prévia da mensagem que será enviada:
                  </div>
                  <pre className="message-preview-box">{fechamento.mensagem_formatada}</pre>
                </div>
              </div>

              {/* Detalhamento dos Atendimentos que compõem o Fechamento */}
              <div className="breakdown-section">
                <h3>📋 Atendimentos & Comissões do Período ({fechamento.itens_receita?.length || 0})</h3>
                {fechamento.itens_receita && fechamento.itens_receita.length > 0 ? (
                  <TableScrollContainer rows={fechamento.itens_receita.length}>
                    <table className="fechamento-table">
                      <thead>
                        <tr>
                          <th>Data</th>
                          <th>Cliente</th>
                          <th>Procedimento</th>
                          <th>Pagamento</th>
                          <th className="text-right">Valor Bruto</th>
                          <th className="text-right">Comissão Somada</th>
                        </tr>
                      </thead>
                      <tbody>
                        {fechamento.itens_receita.map((item) => (
                          <tr key={item.id}>
                            <td>
                              <strong>
                                {item.data
                                  ? new Date(`${item.data}T00:00:00`).toLocaleDateString('pt-BR')
                                  : '—'}
                              </strong>
                            </td>
                            <td>{item.cliente_nome}</td>
                            <td>{item.servico_nome}</td>
                            <td>
                              <span className="payment-badge">{item.forma_pagamento || 'Dinheiro'}</span>
                            </td>
                            <td className="text-right">{formatCurrency(item.valor)}</td>
                            <td className="text-right text-comissao font-bold">
                              {formatCurrency(item.comissao)}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                      <tfoot>
                        <tr>
                          <td colSpan="4">
                            <strong>Total Consolidado do Fechamento:</strong>
                          </td>
                          <td className="text-right font-bold">
                            {formatCurrency(fechamento.totais.faturamento_bruto)}
                          </td>
                          <td className="text-right text-comissao font-bold">
                            {formatCurrency(fechamento.totais.total_comissao)}
                          </td>
                        </tr>
                      </tfoot>
                    </table>
                  </TableScrollContainer>
                ) : (
                  <div className="empty-fechamento">
                    <p>Nenhum atendimento registrado no período de fechamento ({fechamento.periodo.formatado}).</p>
                  </div>
                )}
              </div>
            </>
          ) : (
            <p>Nenhum dado encontrado para o fechamento.</p>
          )}
        </div>
      )}

      {/* ==================== ABA 2: REGRAS & RESPONSÁVEL ==================== */}
      {activeTab === 'regras' && (
        <div className="tab-content regras-tab">
          <form onSubmit={handleSaveConfig} className="config-form">
            {/* Bloco 1: Regras do Fechamento & Comissão */}
            <div className="form-card">
              <div className="card-header">
                <FiCalendar className="card-icon" />
                <div>
                  <h2>Regras do Fechamento do Mês</h2>
                  <p>Defina quando o mês fecha para apuração e a regra de soma das comissões.</p>
                </div>
              </div>

              <div className="form-grid">
                <div className="form-group">
                  <label htmlFor="dia_fechamento_mes">
                    Dia de Fechamento do Mês: <span className="req">*</span>
                  </label>
                  <div className="input-with-shortcuts">
                    <input
                      id="dia_fechamento_mes"
                      type="number"
                      min="1"
                      max="31"
                      value={config.dia_fechamento_mes}
                      onChange={(e) =>
                        setConfig({ ...config, dia_fechamento_mes: parseInt(e.target.value, 10) || '' })
                      }
                      required
                    />
                    <div className="shortcuts">
                      {[20, 25, 28, 30].map((dia) => (
                        <button
                          key={dia}
                          type="button"
                          className={`shortcut-btn ${config.dia_fechamento_mes === dia ? 'active' : ''}`}
                          onClick={() => setConfig({ ...config, dia_fechamento_mes: dia })}
                        >
                          Dia {dia}
                        </button>
                      ))}
                    </div>
                  </div>
                  <span className="field-hint">
                    Dia do mês em que encerra a contabilidade de serviços para mandar para o responsável.
                  </span>
                </div>

                <div className="form-group">
                  <label htmlFor="dia_pagamento_comissao">
                    Dia de Pagamento da Comissão: <span className="req">*</span>
                  </label>
                  <div className="input-with-shortcuts">
                    <input
                      id="dia_pagamento_comissao"
                      type="number"
                      min="1"
                      max="31"
                      value={config.dia_pagamento_comissao}
                      onChange={(e) =>
                        setConfig({ ...config, dia_pagamento_comissao: parseInt(e.target.value, 10) || '' })
                      }
                      required
                    />
                    <div className="shortcuts">
                      {[5, 10, 15, 20].map((dia) => (
                        <button
                          key={dia}
                          type="button"
                          className={`shortcut-btn ${config.dia_pagamento_comissao === dia ? 'active' : ''}`}
                          onClick={() => setConfig({ ...config, dia_pagamento_comissao: dia })}
                        >
                          Dia {dia}
                        </button>
                      ))}
                    </div>
                  </div>
                  <span className="field-hint">
                    Dia do mês seguinte previsto para o pagamento das comissões apuradas.
                  </span>
                </div>

                <div className="form-group">
                  <label htmlFor="percentual_comissao">
                    Percentual de Comissão (%): <span className="req">*</span>
                  </label>
                  <div className="input-with-shortcuts">
                    <input
                      id="percentual_comissao"
                      type="number"
                      min="0"
                      max="100"
                      step="0.5"
                      value={config.percentual_comissao}
                      onChange={(e) =>
                        setConfig({ ...config, percentual_comissao: parseFloat(e.target.value) || '' })
                      }
                      required
                    />
                    <div className="shortcuts">
                      {[15, 20, 25, 30].map((pct) => (
                        <button
                          key={pct}
                          type="button"
                          className={`shortcut-btn ${config.percentual_comissao === pct ? 'active' : ''}`}
                          onClick={() => setConfig({ ...config, percentual_comissao: pct })}
                        >
                          {pct}%
                        </button>
                      ))}
                    </div>
                  </div>
                  <span className="field-hint">
                    Percentual padrão somado sobre o total de serviços realizados no período.
                  </span>
                </div>

                <div className="form-group">
                  <label htmlFor="tipo_periodo_fechamento">
                    Tipo de Período de Apuração: <span className="req">*</span>
                  </label>
                  <select
                    id="tipo_periodo_fechamento"
                    value={config.tipo_periodo_fechamento}
                    onChange={(e) => setConfig({ ...config, tipo_periodo_fechamento: e.target.value })}
                  >
                    <option value="ciclo_fechamento">
                      Ciclo de Fechamento (Do dia seguinte ao fechamento anterior até o dia de fechamento)
                    </option>
                    <option value="mes_calendario">
                      Mês Calendário (Do dia 1º até o dia de fechamento)
                    </option>
                  </select>
                  <span className="field-hint">
                    {config.tipo_periodo_fechamento === 'ciclo_fechamento'
                      ? `Ex: Se o fechamento for dia ${config.dia_fechamento_mes}, apura do dia ${config.dia_fechamento_mes + 1} do mês anterior até o dia ${config.dia_fechamento_mes} deste mês.`
                      : `Ex: Apura do dia 01 até o dia ${config.dia_fechamento_mes} deste mês.`}
                  </span>
                </div>
              </div>
            </div>

            {/* Bloco 2: Dados do Responsável */}
            <div className="form-card">
              <div className="card-header">
                <FiUser className="card-icon" />
                <div>
                  <h2>Dados do Responsável pelo Recebimento</h2>
                  <p>Informe os dados de quem recebe a notificação e o relatório de fechamento.</p>
                </div>
              </div>

              <div className="form-grid">
                <div className="form-group">
                  <label htmlFor="responsavel_nome">Nome do Responsável:</label>
                  <input
                    id="responsavel_nome"
                    type="text"
                    value={config.responsavel_nome}
                    onChange={(e) => setConfig({ ...config, responsavel_nome: e.target.value })}
                    placeholder="Ex: Franci Kelli Müller"
                  />
                </div>

                <div className="form-group">
                  <label htmlFor="responsavel_cargo">Cargo / Função:</label>
                  <input
                    id="responsavel_cargo"
                    type="text"
                    value={config.responsavel_cargo}
                    onChange={(e) => setConfig({ ...config, responsavel_cargo: e.target.value })}
                    placeholder="Ex: Proprietária / Esteticista / Financeiro"
                  />
                </div>

                <div className="form-group">
                  <label htmlFor="responsavel_telefone">WhatsApp do Responsável (com DDD):</label>
                  <input
                    id="responsavel_telefone"
                    type="tel"
                    value={config.responsavel_telefone}
                    onChange={(e) => setConfig({ ...config, responsavel_telefone: e.target.value })}
                    placeholder="Ex: 67999998888 (apenas números)"
                  />
                  <span className="field-hint">Usado para abrir o WhatsApp diretamente com o resumo pronto.</span>
                </div>

                <div className="form-group">
                  <label htmlFor="responsavel_email">E-mail do Responsável:</label>
                  <input
                    id="responsavel_email"
                    type="email"
                    value={config.responsavel_email}
                    onChange={(e) => setConfig({ ...config, responsavel_email: e.target.value })}
                    placeholder="Ex: fran@franbeauty.com.br"
                  />
                </div>
              </div>
            </div>

            {/* Bloco 3: Modelo de Mensagem */}
            <div className="form-card">
              <div className="card-header">
                <FiFileText className="card-icon" />
                <div>
                  <h2>Modelo da Mensagem Enviada ao Responsável</h2>
                  <p>Personalize o texto que é enviado via WhatsApp ou E-mail.</p>
                </div>
              </div>

              <div className="form-group">
                <textarea
                  id="mensagem_fechamento_modelo"
                  rows="8"
                  value={config.mensagem_fechamento_modelo}
                  onChange={(e) => setConfig({ ...config, mensagem_fechamento_modelo: e.target.value })}
                />
                <div className="tags-help">
                  <strong>Variáveis disponíveis:</strong>
                  <code>{'{responsavel}'}</code>
                  <code>{'{periodo}'}</code>
                  <code>{'{data_fechamento}'}</code>
                  <code>{'{qtd_servicos}'}</code>
                  <code>{'{faturamento_bruto}'}</code>
                  <code>{'{total_comissao}'}</code>
                  <code>{'{total_despesas}'}</code>
                  <code>{'{lucro_liquido}'}</code>
                  <code>{'{data_pagamento}'}</code>
                </div>
              </div>
            </div>

            <div className="form-actions">
              <button type="submit" className="save-btn" disabled={saving}>
                {saving ? <FiRefreshCw className="spinning" size={18} /> : <FiCheck size={18} />}
                <span>{saving ? 'Salvando...' : 'Salvar Configurações'}</span>
              </button>
            </div>
          </form>
        </div>
      )}
    </div>
  );
}

export default Configuracoes;
